import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import ChatIcon from '@material-ui/icons/Chat';
import CallEndIcon from '@material-ui/icons/CallEnd';
import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import PhoneIcon from '@material-ui/icons/Phone';
import SendIcon from '@material-ui/icons/Send';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import EventIcon from '@material-ui/icons/Event';
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {Trans, useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {
  LocalAudioTrack,
  RemoteTrack,
  Room,
  RoomEvent,
  Track
} from 'livekit-client';
import PhoneInputField from '@components/system/PhoneInputField/PhoneInputField';
import PhoneFormatter from '@components/system/PhoneFormatter/PhoneFormatter';
import Loader from '@components/framework/loader/Loader';
import HelperUtils from '@shared/utils/HelperUtils';
import {VOICE_ASSISTANT_LANGUAGES} from '@shared/constants/Mapping';
import {
  createVoiceAssistantWebConnection,
  fetchVoiceAssistantWebConfig,
  fetchVoiceAssistantWebPrefill,
  sendVoiceAssistantWebOtp,
  startVoiceAssistantWebAgent,
  verifyVoiceAssistantWebOtp,
  VoiceAssistantClinicConfig,
  VoiceAssistantConnectionDetails,
  VoiceAssistantInteractionMode
} from '@shared/services/VoiceAssistantWeb.service';
import PublicPageLayout from '../PublicPageLayout/PublicPageLayout';
import {renderMarkdown} from '../../../utils/MarkdownUtils';
import './VoiceAssistant.scss';

type VoiceAssistantParams = {
  shortCode: string;
  paymentToken?: string;
};

type SessionStatus = 'welcome' | 'connecting' | 'connected' | 'completed';

type PhoneVerificationStep = 'phone' | 'otp' | 'verified';

const DEFAULT_VOICE_ASSISTANT_LANGUAGE = 'en';

function toVoiceAssistantLanguageCode(value?: string): string {
  return value === 'Spanish' || value === 'es' ? 'es' : 'en';
}

function getDefaultVoiceAssistantLanguage(
  availableLanguages?: string[]
): string {
  if (availableLanguages?.includes(DEFAULT_VOICE_ASSISTANT_LANGUAGE)) {
    return DEFAULT_VOICE_ASSISTANT_LANGUAGE;
  }
  return availableLanguages?.[0] || DEFAULT_VOICE_ASSISTANT_LANGUAGE;
}

function getVoiceAssistantLanguageOptions(availableLanguages?: string[]) {
  const allowedCodes = new Set(
    (availableLanguages?.length
      ? availableLanguages
      : [DEFAULT_VOICE_ASSISTANT_LANGUAGE]
    ).map(toVoiceAssistantLanguageCode)
  );
  return VOICE_ASSISTANT_LANGUAGES.filter(item =>
    allowedCodes.has(toVoiceAssistantLanguageCode(item.value))
  ).map(item => ({
    label: item.label,
    value: toVoiceAssistantLanguageCode(item.value)
  }));
}

type TranscriptMessage = {
  id: string;
  message: string;
  origin: 'local' | 'remote' | 'system';
  timestamp: number;
};

type UiDirectiveKind =
  | 'slot_select'
  | 'provider_select'
  | 'choice'
  | 'date_select'
  | 'dob_select';

type UiOption = {
  id: string;
  label: string;
  send_value: string;
};

type DateConstraints = {
  min_offset_days: number;
  max_offset_days: number;
  disable_weekends: boolean;
  disabled_dates: string[];
};

type DobConstraints = {
  max_age_years: number;
  is_pediatric: boolean;
};

type UiDirective = {
  type: 'ui_options';
  kind: UiDirectiveKind;
  prompt: string;
  show_prompt?: boolean;
  options: UiOption[];
  directive_id: string;
  constraints?: DateConstraints;
  dobConstraints?: DobConstraints;
  multiple?: boolean;
  other?: boolean;
  other_label?: string;
  submit_label?: string;
};

const CHAT_TOPIC = 'lk.chat';
const TRANSCRIPTION_TOPIC = 'lk.transcription';
const UI_OPTIONS_TOPIC = 'ui_options';
const MICROPHONE_CAPTURE_OPTIONS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true
};
const UI_DIRECTIVE_KINDS: UiDirectiveKind[] = [
  'slot_select',
  'provider_select',
  'choice',
  'date_select',
  'dob_select'
];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

function hasConversationHappened(messages: TranscriptMessage[]): boolean {
  return messages.some(
    item => item.origin === 'local' || item.origin === 'remote'
  );
}

function getPhoneNumber(phone: string): string {
  if (!phone) {
    return '';
  }
  const trimmed = phone.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  if (trimmed.startsWith('+')) {
    return `+${digits}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return `+${digits}`;
}

function getPhoneInputValue(phone: string): string {
  return getPhoneNumber(phone).replace(/\D/g, '');
}

function isValidPatientPhone(phone: string): boolean {
  if (!phone || !phone.replace(/\s/g, '').length) {
    return false;
  }
  return Boolean(phone.replace(/[^0-9]+/g, ''));
}

function getPatientPhoneError(
  phone: string,
  translate: (key: string) => string
): string {
  const raw = (phone || '').trim();
  const digitsOnly = raw.replace(/\D/g, '');
  if (!raw || digitsOnly.length <= 1) {
    return translate('VOICE_ASSISTANT_WEB.PATIENT_PHONE_REQUIRED');
  }
  if (!isValidPatientPhone(phone)) {
    return translate('VOICE_ASSISTANT_WEB.PATIENT_PHONE_INVALID');
  }
  return '';
}

function makeMessageId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function parseUiOptions(value: unknown): UiOption[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isUiOption);
}

function isUiOption(value: unknown): value is UiOption {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const option = value as Record<string, unknown>;
  return (
    typeof option.id === 'string' &&
    typeof option.label === 'string' &&
    typeof option.send_value === 'string'
  );
}

function toFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && isFinite(value) ? value : fallback;
}

function parseDateConstraints(value: unknown): DateConstraints | undefined {
  if (typeof value !== 'object' || value === null) {
    return undefined;
  }
  const constraints = value as Record<string, unknown>;
  return {
    min_offset_days: toFiniteNumber(constraints.min_offset_days, 0),
    max_offset_days: toFiniteNumber(constraints.max_offset_days, 90),
    disable_weekends: constraints.disable_weekends === true,
    disabled_dates: Array.isArray(constraints.disabled_dates)
      ? constraints.disabled_dates.filter(
          (date): date is string => typeof date === 'string'
        )
      : []
  };
}

function parseDobConstraints(value: unknown): DobConstraints {
  const constraints = (
    typeof value === 'object' && value !== null ? value : {}
  ) as Record<string, unknown>;
  return {
    max_age_years: toFiniteNumber(constraints.max_age_years, 120),
    is_pediatric: constraints.is_pediatric === true
  };
}

/** Backend sets prompt only when a summarized sub-heading adds clarity. */
function shouldShowUiPrompt(directive: UiDirective): boolean {
  if (directive.show_prompt === false) {
    return false;
  }
  if (!directive.prompt?.trim()) {
    return false;
  }
  if (directive.show_prompt === true) {
    return true;
  }
  // Agent prose is already in the transcript — only calendar kinds may show a label.
  return directive.kind === 'date_select' || directive.kind === 'dob_select';
}

function formatMessageTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** Show timestamp once at the end of each consecutive same-origin group. */
function shouldShowMessageTimestamp(
  items: TranscriptMessage[],
  index: number
): boolean {
  const current = items[index];
  const next = items[index + 1];
  if (!current) {
    return false;
  }
  if (!next) {
    return true;
  }
  return next.origin !== current.origin;
}

function isQuickReplyDirective(directive: UiDirective | null): boolean {
  return directive?.kind === 'choice';
}

function choiceWaitsForSubmit(directive: UiDirective): boolean {
  return directive.multiple === true || directive.other === true;
}

function isThreadUiDirective(directive: UiDirective | null): boolean {
  if (!directive) {
    return false;
  }
  return (
    directive.kind === 'provider_select' ||
    directive.kind === 'slot_select' ||
    directive.kind === 'date_select' ||
    directive.kind === 'dob_select'
  );
}

function parseUiDirective(raw: string): UiDirective | null {
  try {
    const data = JSON.parse(raw) as Record<string, unknown>;
    if (
      data.type !== 'ui_options' ||
      !UI_DIRECTIVE_KINDS.includes(data.kind as UiDirectiveKind)
    ) {
      return null;
    }

    const kind = data.kind as UiDirectiveKind;
    const base = {
      type: 'ui_options' as const,
      kind,
      prompt: typeof data.prompt === 'string' ? data.prompt : '',
      show_prompt:
        typeof data.show_prompt === 'boolean' ? data.show_prompt : undefined,
      directive_id:
        typeof data.directive_id === 'string'
          ? data.directive_id
          : String(Date.now())
    };

    if (kind === 'date_select') {
      return {
        ...base,
        options: parseUiOptions(data.options),
        constraints: parseDateConstraints(data.constraints)
      };
    }
    if (kind === 'dob_select') {
      return {
        ...base,
        options: [],
        dobConstraints: parseDobConstraints(data.constraints)
      };
    }
    if (
      !Array.isArray(data.options) ||
      !data.options.every(isUiOption) ||
      data.options.length === 0
    ) {
      return null;
    }
    return {
      ...base,
      options: data.options as UiOption[],
      multiple: data.multiple === true,
      other: data.other === true,
      other_label:
        typeof data.other_label === 'string' ? data.other_label : undefined,
      submit_label:
        typeof data.submit_label === 'string' ? data.submit_label : undefined
    };
  } catch {
    return null;
  }
}

function startOfLocalDay(date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function padTwoDigits(value: number): string {
  return value < 10 ? `0${value}` : `${value}`;
}

function toDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${padTwoDigits(
    date.getMonth() + 1
  )}-${padTwoDigits(date.getDate())}`;
}

function parseDateInputValue(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const date = new Date(year, month, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }
  return startOfLocalDay(date);
}

function getOrdinalSuffix(day: number): string {
  const remainder = day % 100;
  if (remainder >= 11 && remainder <= 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function formatDateWithOrdinal(date: Date): string {
  const day = date.getDate();
  return `${MONTH_NAMES[date.getMonth()]} ${day}${getOrdinalSuffix(
    day
  )}, ${date.getFullYear()}`;
}

function getDateBounds(constraints?: DateConstraints): {
  minDate: Date;
  maxDate: Date;
  disabledDates: Set<string>;
  disableWeekends: boolean;
} {
  const today = startOfLocalDay();
  const normalized = constraints || {
    min_offset_days: 0,
    max_offset_days: 90,
    disable_weekends: false,
    disabled_dates: []
  };
  return {
    minDate: addDays(today, normalized.min_offset_days),
    maxDate: addDays(today, normalized.max_offset_days),
    disabledDates: new Set(normalized.disabled_dates),
    disableWeekends: normalized.disable_weekends
  };
}

function getDobBounds(constraints?: DobConstraints): {
  minDate: Date;
  maxDate: Date;
} {
  const today = startOfLocalDay();
  const maxAge = constraints?.max_age_years ?? 120;
  return {
    minDate: startOfLocalDay(new Date(today.getFullYear() - maxAge, 0, 1)),
    // A birth date cannot be today or in the future.
    maxDate: addDays(today, -1)
  };
}

function openNativeDatePicker(input: HTMLInputElement): void {
  const picker = input as HTMLInputElement & {showPicker?: () => void};
  if (typeof picker.showPicker !== 'function') {
    return;
  }
  try {
    picker.showPicker();
  } catch (e) {
    // The picker is already open, or this browser blocked the call.
  }
}

function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return true;
  }
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function isDateSelectable(date: Date, constraints?: DateConstraints): boolean {
  const {minDate, maxDate, disabledDates} = getDateBounds(constraints);
  if (date < minDate || date > maxDate) {
    return false;
  }
  // if (disableWeekends && (date.getDay() === 0 || date.getDay() === 6)) {
  //   return false;
  // }
  return !disabledDates.has(toDateInputValue(date));
}

const VoiceAssistant = (): ReactElement => {
  const {shortCode, paymentToken = ''} = useParams<VoiceAssistantParams>();
  const {t} = useTranslation();

  const [clinicConfig, setClinicConfig] =
    useState<VoiceAssistantClinicConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(
    DEFAULT_VOICE_ASSISTANT_LANGUAGE
  );
  const [patientPhone, setPatientPhone] = useState('');
  const [prefilledPhone, setPrefilledPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [phoneVerificationStep, setPhoneVerificationStep] =
    useState<PhoneVerificationStep>('phone');
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [verificationSuccessMessage, setVerificationSuccessMessage] =
    useState('');
  const [welcomeInlineError, setWelcomeInlineError] = useState('');
  const [sessionInlineError, setSessionInlineError] = useState('');
  const [uiDateError, setUiDateError] = useState('');
  const [uiDateValue, setUiDateValue] = useState('');
  const [resendCooldownRemaining, setResendCooldownRemaining] = useState(0);
  const [status, setStatus] = useState<SessionStatus>('welcome');
  const [activeMode, setActiveMode] =
    useState<VoiceAssistantInteractionMode>('chat');
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [messageDraft, setMessageDraft] = useState('');
  const [micEnabled, setMicEnabled] = useState(true);
  const [isRoomLive, setIsRoomLive] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [uiDirective, setUiDirective] = useState<UiDirective | null>(null);
  const [answeredUiOptionId, setAnsweredUiOptionId] = useState('');
  const [pickedChoiceIds, setPickedChoiceIds] = useState<string[]>([]);
  const [otherChoiceOn, setOtherChoiceOn] = useState(false);
  const [otherChoiceText, setOtherChoiceText] = useState('');
  const [isMobileKeyboardOpen, setIsMobileKeyboardOpen] = useState(false);
  const [isViewportZoomed, setIsViewportZoomed] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(max-width: 960px)').matches;
  });
  const [isPhoneViewport, setIsPhoneViewport] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(max-width: 767px)').matches;
  });

  const languageOptions = getVoiceAssistantLanguageOptions(
    clinicConfig?.available_languages
  );

  const roomRef = useRef<Room | null>(null);
  const localAudioTrackRef = useRef<LocalAudioTrack | null>(null);
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLInputElement | null>(null);
  const lastRemoteMessageRef = useRef<{
    message: string;
    timestamp: number;
  } | null>(null);
  const audioPlaybackBlockedRef = useRef(false);
  const endedByUserRef = useRef(false);
  const messagesRef = useRef<TranscriptMessage[]>([]);
  const statusRef = useRef<SessionStatus>('welcome');
  const activeAgentStreamsRef = useRef(0);
  const dispatchedRoomNamesRef = useRef<Set<string>>(new Set());
  const dateFieldFocusedAtRef = useRef(0);
  const dateFieldTouchedRef = useRef(false);
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const commitDesktopDateRef = useRef<(value: string) => void>(() => undefined);

  const startConnectedAgent = (
    connection: VoiceAssistantConnectionDetails
  ): Promise<void> => {
    if (dispatchedRoomNamesRef.current.has(connection.roomName)) {
      return Promise.resolve();
    }
    if (!connection.dispatchToken) {
      return Promise.reject(new Error('Unable to start the assistant.'));
    }
    dispatchedRoomNamesRef.current.add(connection.roomName);
    return startVoiceAssistantWebAgent({
      shortCode,
      dispatchToken: connection.dispatchToken
    }).then(() => undefined);
  };

  const otpResendCooldownSeconds = clinicConfig?.otp_send_cooldown_seconds;

  const startResendCooldown = useCallback(() => {
    setResendCooldownRemaining(otpResendCooldownSeconds);
  }, [otpResendCooldownSeconds]);

  useEffect(() => {
    if (resendCooldownRemaining <= 0) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setResendCooldownRemaining(current => current - 1);
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [resendCooldownRemaining]);

  const showSuccessScreen = useCallback(() => {
    if (
      endedByUserRef.current ||
      statusRef.current === 'completed' ||
      statusRef.current !== 'connected'
    ) {
      return;
    }
    setStatus('completed');
  }, []);

  const appendMessage = useCallback(
    (message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => {
      setMessages(current => [
        ...current,
        {
          ...message,
          id: makeMessageId(message.origin),
          timestamp: Date.now()
        }
      ]);
    },
    []
  );

  const appendRemoteMessage = useCallback(
    (text: string) => {
      const message = text.trim();
      if (!message) {
        return;
      }

      const now = Date.now();
      const lastRemoteMessage = lastRemoteMessageRef.current;
      if (
        lastRemoteMessage &&
        lastRemoteMessage.message === message &&
        now - lastRemoteMessage.timestamp < 1500
      ) {
        return;
      }

      lastRemoteMessageRef.current = {message, timestamp: now};
      appendMessage({origin: 'remote', message});
      if (activeAgentStreamsRef.current === 0) {
        setIsAgentTyping(false);
      }
    },
    [appendMessage]
  );

  const resetChoiceDraft = useCallback(() => {
    setPickedChoiceIds([]);
    setOtherChoiceOn(false);
    setOtherChoiceText('');
  }, []);

  const clearUiDirective = useCallback(() => {
    setUiDirective(null);
    setAnsweredUiOptionId('');
    setUiDateError('');
    setUiDateValue('');
    dateFieldTouchedRef.current = false;
    resetChoiceDraft();
  }, [resetChoiceDraft]);

  const showUiDirective = useCallback((directive: UiDirective) => {
    setUiDirective(directive);
    setAnsweredUiOptionId('');
    setUiDateError('');
    setUiDateValue('');
    dateFieldTouchedRef.current = false;
    resetChoiceDraft();
    if (activeAgentStreamsRef.current === 0) {
      setIsAgentTyping(false);
    }
  }, [resetChoiceDraft]);

  const streamRemoteMessage = useCallback(
    async (reader: any, sourceRoom: Room) => {
      if (roomRef.current !== sourceRoom) {
        return;
      }

      const messageId = makeMessageId('remote');
      const timestamp = Date.now();
      let streamedText = '';
      let messageAdded = false;

      activeAgentStreamsRef.current += 1;
      setIsAgentTyping(true);

      try {
        for await (const chunk of reader) {
          if (roomRef.current !== sourceRoom || endedByUserRef.current) {
            return;
          }
          if (typeof chunk !== 'string' || !chunk) {
            continue;
          }

          streamedText += chunk;
          if (!streamedText.trim()) {
            continue;
          }

          if (!messageAdded) {
            messageAdded = true;
            setMessages(current => [
              ...current,
              {
                id: messageId,
                message: streamedText,
                origin: 'remote',
                timestamp
              }
            ]);
          } else {
            setMessages(current =>
              current.map(item =>
                item.id === messageId ? {...item, message: streamedText} : item
              )
            );
          }
        }

        const finalMessage = streamedText.trim();
        if (messageAdded && finalMessage) {
          setMessages(current =>
            current.map(item =>
              item.id === messageId ? {...item, message: finalMessage} : item
            )
          );
          lastRemoteMessageRef.current = {
            message: finalMessage,
            timestamp: Date.now()
          };
        }
      } finally {
        if (roomRef.current === sourceRoom) {
          activeAgentStreamsRef.current = Math.max(
            0,
            activeAgentStreamsRef.current - 1
          );
          if (activeAgentStreamsRef.current === 0) {
            setIsAgentTyping(false);
          }
        }
      }
    },
    []
  );

  const resetSessionView = useCallback(() => {
    setMessages([]);
    setMessageDraft('');
    setMicEnabled(true);
    setIsAgentTyping(false);
    setSessionInlineError('');
    clearUiDirective();
    activeAgentStreamsRef.current = 0;
    lastRemoteMessageRef.current = null;
    audioPlaybackBlockedRef.current = false;
  }, [clearUiDirective]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const cleanupRoom = useCallback(() => {
    if (localAudioTrackRef.current) {
      localAudioTrackRef.current.stop();
      localAudioTrackRef.current = null;
    }
    if (roomRef.current) {
      roomRef.current.disconnect();
      (roomRef.current as any).removeAllListeners?.();
      roomRef.current = null;
    }
    if (audioContainerRef.current) {
      audioContainerRef.current.innerHTML = '';
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadConfig() {
      try {
        const configPromise = fetchVoiceAssistantWebConfig({shortCode});
        const prefillPromise = paymentToken
          ? fetchVoiceAssistantWebPrefill({shortCode, paymentToken}).catch(
              () => null
            )
          : Promise.resolve(null);
        const [configResponse, prefillResponse] = await Promise.all([
          configPromise,
          prefillPromise
        ]);
        if (!mounted) return;
        setClinicConfig(configResponse.data);
        const prefillPhone = getPhoneInputValue(
          prefillResponse?.data?.patient_phone || ''
        );
        if (prefillPhone) {
          setPatientPhone(prefillPhone);
          setPrefilledPhone(prefillPhone);
        } else {
          setPrefilledPhone('');
        }
        setSelectedLanguage(
          getDefaultVoiceAssistantLanguage(
            configResponse?.data?.available_languages
          )
        );
      } catch (e) {
        if (mounted) {
          setPageError(e?.message || 'Unable to load this voice assistant.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadConfig();
    return () => {
      mounted = false;
      cleanupRoom();
    };
  }, [cleanupRoom, paymentToken, shortCode]);

  const scrollTranscriptToBottom = useCallback(() => {
    const container = transcriptContainerRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
    if (container.scrollLeft !== 0) {
      container.scrollLeft = 0;
    }
  }, []);

  const focusChatInput = useCallback(() => {
    if (activeMode !== 'chat') {
      return;
    }
    window.requestAnimationFrame(() => {
      chatInputRef.current?.focus({preventScroll: true});
    });
  }, [activeMode]);

  useEffect(() => {
    if (status === 'connected' && activeMode === 'chat') {
      focusChatInput();
    }
  }, [status, activeMode, focusChatInput]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(max-width: 960px)');
    const syncViewport = () => setIsMobileViewport(mediaQuery.matches);
    syncViewport();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncViewport);
      return () => mediaQuery.removeEventListener('change', syncViewport);
    }
    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const syncViewport = () => setIsPhoneViewport(mediaQuery.matches);
    syncViewport();
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncViewport);
      return () => mediaQuery.removeEventListener('change', syncViewport);
    }
    mediaQuery.addListener(syncViewport);
    return () => mediaQuery.removeListener(syncViewport);
  }, []);

  const isSessionPanelOpen = status === 'connected' || status === 'completed';
  const isMobileChatLayoutRef = useRef(false);
  isMobileChatLayoutRef.current = activeMode === 'chat' && isMobileViewport;

  useEffect(() => {
    if (!isSessionPanelOpen) {
      setIsMobileKeyboardOpen(false);
      document.documentElement.style.removeProperty(
        '--voice-assistant-shell-height'
      );
      document.documentElement.style.removeProperty(
        '--voice-assistant-shell-width'
      );
      document.documentElement.style.removeProperty(
        '--voice-assistant-shell-top'
      );
      document.documentElement.style.removeProperty(
        '--voice-assistant-shell-left'
      );
      document.documentElement.style.removeProperty(
        '--voice-assistant-shell-shift'
      );
      return undefined;
    }

    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlOverscroll =
      document.documentElement.style.overscrollBehavior;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyOverscroll = document.body.style.overscrollBehavior;

    const lockPageScroll = () => {
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.overscrollBehavior = 'none';
      document.body.style.overflow = 'hidden';
      document.body.style.overscrollBehavior = 'none';
    };
    const unlockPageScroll = () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.overscrollBehavior =
        previousHtmlOverscroll;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
    };
    lockPageScroll();

    let keyboardWasOpen = false;
    let pageZoomed = false;
    const initialViewport = window.visualViewport;
    let fullScreenHeight = Math.max(
      window.innerHeight,
      initialViewport?.height || 0
    );

    const clearShellOffset = () => {
      const rootStyle = document.documentElement.style;
      rootStyle.removeProperty('--voice-assistant-shell-height');
      rootStyle.removeProperty('--voice-assistant-shell-width');
      rootStyle.removeProperty('--voice-assistant-shell-top');
      rootStyle.removeProperty('--voice-assistant-shell-left');
      rootStyle.removeProperty('--voice-assistant-shell-shift');
    };

    const syncShellToViewport = () => {
      const activeElement = document.activeElement;
      const datePickerOpen = Boolean(
        activeElement && activeElement.closest('.voice-assistant-ui-date-field')
      );
      // Resizing while the native iOS date popup is open moves the field
      // and iOS closes it, committing today.
      if (datePickerOpen) {
        return;
      }

      const visualViewport = window.visualViewport;
      const viewportHeight = visualViewport?.height ?? window.innerHeight;
      const offsetTop = visualViewport?.offsetTop || 0;
      const viewportScale = visualViewport?.scale ?? 1;
      // Pinch-zoom shrinks the visual viewport the same way the keyboard
      // does. Resizing the chat then traps the page at that zoom.
      if (viewportScale > 1) {
        clearShellOffset();
        setIsMobileKeyboardOpen(false);
        keyboardWasOpen = false;
        if (!pageZoomed) {
          pageZoomed = true;
          unlockPageScroll();
          setIsViewportZoomed(true);
        }
        return;
      }
      if (pageZoomed) {
        pageZoomed = false;
        lockPageScroll();
        setIsViewportZoomed(false);
      }
      if (viewportHeight > fullScreenHeight) {
        fullScreenHeight = viewportHeight;
      }
      // Safari's toolbar is much shorter than the keyboard. Only the
      // keyboard should change the chat size.
      const keyboardOpen = fullScreenHeight - viewportHeight > 220;

      setIsMobileKeyboardOpen(keyboardOpen);
      if (!keyboardOpen) {
        clearShellOffset();
        keyboardWasOpen = false;
        return;
      }

      const rootStyle = document.documentElement.style;
      rootStyle.setProperty(
        '--voice-assistant-shell-height',
        `${Math.round(viewportHeight)}px`
      );
      rootStyle.setProperty(
        '--voice-assistant-shell-shift',
        `${Math.round(offsetTop)}px`
      );

      const transcript = transcriptContainerRef.current;
      if (transcript && transcript.scrollLeft !== 0) {
        transcript.scrollLeft = 0;
      }
      if (!keyboardWasOpen) {
        window.requestAnimationFrame(() => {
          const messageList = transcriptContainerRef.current;
          if (messageList) {
            messageList.scrollTop = messageList.scrollHeight;
          }
        });
      }
      keyboardWasOpen = true;
    };

    const preventPageScroll = (event: TouchEvent) => {
      if (!isMobileChatLayoutRef.current) {
        return;
      }
      // A pinch, or a drag while already zoomed, must move the page.
      if (event.touches.length > 1 || (window.visualViewport?.scale ?? 1) > 1) {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (!target) {
        event.preventDefault();
        return;
      }
      const transcript = transcriptContainerRef.current;
      if (transcript && transcript.contains(target)) {
        return;
      }
      if (target.closest('.voice-assistant-quick-replies-list')) {
        return;
      }
      if (target.closest('.voice-assistant-composer')) {
        return;
      }
      event.preventDefault();
    };

    let focusFrame = 0;
    const syncAfterFocusChange = () => {
      window.cancelAnimationFrame(focusFrame);
      focusFrame = window.requestAnimationFrame(syncShellToViewport);
    };

    syncShellToViewport();

    const visualViewport = window.visualViewport;
    window.addEventListener('resize', syncShellToViewport);
    visualViewport?.addEventListener('resize', syncShellToViewport);
    visualViewport?.addEventListener('scroll', syncShellToViewport);
    document.addEventListener('focusout', syncAfterFocusChange);
    document.addEventListener('touchmove', preventPageScroll, {
      passive: false
    });

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.overscrollBehavior =
        previousHtmlOverscroll;
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscroll;
      clearShellOffset();
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('resize', syncShellToViewport);
      visualViewport?.removeEventListener('resize', syncShellToViewport);
      visualViewport?.removeEventListener('scroll', syncShellToViewport);
      document.removeEventListener('focusout', syncAfterFocusChange);
      document.removeEventListener('touchmove', preventPageScroll);
      setIsViewportZoomed(false);
    };
  }, [isSessionPanelOpen]);

  useLayoutEffect(() => {
    scrollTranscriptToBottom();
    const frameId = window.requestAnimationFrame(scrollTranscriptToBottom);
    return () => window.cancelAnimationFrame(frameId);
  }, [messages, isAgentTyping, uiDirective, scrollTranscriptToBottom]);

  useEffect(() => {
    const container = transcriptContainerRef.current;
    if (!container || status !== 'connected') {
      return undefined;
    }

    let frameId: number | null = null;
    const scheduleScroll = () => {
      if (frameId !== null) {
        return;
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        scrollTranscriptToBottom();
      });
    };

    const observer = new MutationObserver(scheduleScroll);

    observer.observe(container, {
      childList: true,
      subtree: true,
      characterData: true
    });

    scrollTranscriptToBottom();

    return () => {
      observer.disconnect();
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [scrollTranscriptToBottom, status]);

  const attachRemoteAudioTrack = useCallback(
    (track: RemoteTrack) => {
      if (track.kind !== Track.Kind.Audio || !audioContainerRef.current) {
        return;
      }
      const element = track.attach();
      element.autoplay = true;
      audioContainerRef.current.appendChild(element);
      const playPromise = element.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          if (audioPlaybackBlockedRef.current) {
            return;
          }
          audioPlaybackBlockedRef.current = true;
          appendMessage({
            origin: 'system',
            message:
              'Audio playback was blocked by the browser. Click the page and start voice again.'
          });
        });
      }
    },
    [appendMessage]
  );

  const detachRemoteAudioTrack = useCallback((track: RemoteTrack) => {
    track.detach().forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
  }, []);

  const handleDataReceived = useCallback(
    (payload: Uint8Array, participant?: any, _kind?: any, topic?: string) => {
      const text = new TextDecoder().decode(payload);
      if (topic === 'session_info') {
        return;
      }

      if (participant && participant.isLocal) {
        return;
      }

      if (topic === UI_OPTIONS_TOPIC) {
        const directive = parseUiDirective(text);
        if (directive) {
          showUiDirective(directive);
        }
        return;
      }

      if (topic && topic !== CHAT_TOPIC) {
        return;
      }

      try {
        const data = JSON.parse(text);
        const message = data.message || data.text || data.content;
        if (message) {
          appendRemoteMessage(message);
        }
      } catch (e) {
        if (text.trim()) {
          appendRemoteMessage(text);
        }
      }
    },
    [appendRemoteMessage, showUiDirective]
  );

  const registerTextStreamHandler = useCallback(
    (room: Room) => {
      const registerHandler = (room as any).registerTextStreamHandler;
      if (typeof registerHandler !== 'function') {
        return;
      }
      registerHandler.call(
        room,
        CHAT_TOPIC,
        async (reader: any, participantInfo: any) => {
          try {
            if (
              participantInfo &&
              participantInfo.identity === room.localParticipant.identity
            ) {
              return;
            }
            await streamRemoteMessage(reader, room);
          } catch (e) {
            return;
          }
        }
      );
    },
    [streamRemoteMessage]
  );

  const registerTranscriptionStreamHandler = useCallback(
    (room: Room) => {
      const registerHandler = (room as any).registerTextStreamHandler;
      if (typeof registerHandler !== 'function') {
        return;
      }
      registerHandler.call(
        room,
        TRANSCRIPTION_TOPIC,
        async (reader: any, participantInfo: any) => {
          try {
            if (
              participantInfo &&
              participantInfo.identity === room.localParticipant.identity
            ) {
              return;
            }
            await streamRemoteMessage(reader, room);
          } catch (e) {
            return;
          }
        }
      );
    },
    [streamRemoteMessage]
  );

  const registerTranscriptionHandler = useCallback(
    (room: Room) => {
      const transcriptionEvent = (RoomEvent as any).TranscriptionReceived;
      if (!transcriptionEvent) {
        return;
      }
      room.on(transcriptionEvent, ((segments: any[], participant: any) => {
        if (!Array.isArray(segments)) {
          return;
        }
        segments.forEach(segment => {
          if (!segment || !segment.final || !segment.text) {
            return;
          }
          if (participant && participant.isLocal) {
            appendMessage({origin: 'local', message: segment.text});
          } else {
            appendRemoteMessage(segment.text);
          }
        });
      }) as any);
    },
    [appendMessage, appendRemoteMessage]
  );

  const enableMicrophone = useCallback(async (room: Room) => {
    const publication = await room.localParticipant.setMicrophoneEnabled(
      true,
      MICROPHONE_CAPTURE_OPTIONS
    );
    const track = (publication && publication.track) as
      | LocalAudioTrack
      | undefined;
    if (!track) {
      throw new Error('Microphone could not be enabled.');
    }
    localAudioTrackRef.current = track;
    setMicEnabled(true);
  }, []);

  const resetPhoneVerification = useCallback(() => {
    setOtp('');
    setOtpError('');
    setPhoneError('');
    setWelcomeInlineError('');
    setVerificationToken('');
    setVerificationSuccessMessage('');
    setPhoneVerificationStep('phone');
    setVerifiedPhone('');
    setResendCooldownRemaining(0);
  }, []);

  const handlePatientPhoneChange = useCallback(
    (value: string) => {
      setPatientPhone(value);
      if (phoneError) {
        setPhoneError('');
      }
      if (welcomeInlineError) {
        setWelcomeInlineError('');
      }
      if (verifiedPhone && getPhoneNumber(value) !== verifiedPhone) {
        resetPhoneVerification();
      }
    },
    [phoneError, resetPhoneVerification, verifiedPhone, welcomeInlineError]
  );

  const handleSendOtp = async (isResend = false) => {
    const formattedPatientPhone = getPhoneNumber(patientPhone);
    const phoneValidationError = getPatientPhoneError(patientPhone, t);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }

    setPhoneError('');
    setIsSendingOtp(true);
    try {
      const response = await sendVoiceAssistantWebOtp({
        shortCode,
        patientPhone: formattedPatientPhone,
        resend: isResend
      });
      setPhoneVerificationStep('otp');
      startResendCooldown();
      setOtpError('');
      setVerificationSuccessMessage(
        response.message || t('VOICE_ASSISTANT_WEB.VERIFICATION_CODE_SENT')
      );
    } catch (e) {
      setPhoneError(
        e?.message || t('VOICE_ASSISTANT_WEB.SEND_VERIFICATION_CODE_FAILED')
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    const formattedPatientPhone = getPhoneNumber(patientPhone);
    const normalizedOtp = otp.trim();
    const phoneValidationError = getPatientPhoneError(patientPhone, t);
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    if (!normalizedOtp) {
      setOtpError(t('VOICE_ASSISTANT_WEB.VERIFICATION_CODE_PLACEHOLDER'));
      return;
    }

    setOtpError('');
    setIsVerifyingOtp(true);
    try {
      const response = await verifyVoiceAssistantWebOtp({
        shortCode,
        patientPhone: formattedPatientPhone,
        otp: normalizedOtp
      });
      const token = response.data.verification_token;
      setVerificationToken(token);
      setVerifiedPhone(formattedPatientPhone);
      setPhoneVerificationStep('verified');
      setOtp('');
      setOtpError('');
      setVerificationSuccessMessage(
        response.message || t('VOICE_ASSISTANT_WEB.PHONE_VERIFIED')
      );

      const voiceEnabled = clinicConfig?.voice_button_enabled !== false;
      if (!voiceEnabled) {
        await startSession('chat', {
          verificationToken: token,
          patientPhone: formattedPatientPhone
        });
      }
    } catch (e) {
      setOtpError(
        e?.message || t('VOICE_ASSISTANT_WEB.INVALID_VERIFICATION_CODE')
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const startSession = async (
    interactionMode: VoiceAssistantInteractionMode,
    sessionOverrides?: {
      verificationToken?: string;
      patientPhone?: string;
    }
  ) => {
    const formattedPatientPhone =
      sessionOverrides?.patientPhone || getPhoneNumber(patientPhone);
    const activeVerificationToken =
      sessionOverrides?.verificationToken || verificationToken;

    const phoneValidationError = getPatientPhoneError(
      sessionOverrides?.patientPhone || patientPhone,
      t
    );
    if (phoneValidationError) {
      setPhoneError(phoneValidationError);
      return;
    }
    if (!activeVerificationToken) {
      setPhoneError(t('VOICE_ASSISTANT_WEB.VERIFY_PHONE_TO_CONTINUE'));
      return;
    }
    if (
      !sessionOverrides &&
      (phoneVerificationStep !== 'verified' ||
        verifiedPhone !== formattedPatientPhone)
    ) {
      setPhoneError(t('VOICE_ASSISTANT_WEB.VERIFY_PHONE_TO_CONTINUE'));
      return;
    }

    endedByUserRef.current = false;
    setWelcomeInlineError('');
    setStatus('connecting');
    setActiveMode(interactionMode);
    setIsRoomLive(false);
    setIsAgentTyping(interactionMode === 'chat');
    activeAgentStreamsRef.current = 0;
    setMessages([]);
    setSessionInlineError('');
    clearUiDirective();
    cleanupRoom();

    try {
      const response = await createVoiceAssistantWebConnection({
        shortCode,
        patientPhone: formattedPatientPhone,
        interactionMode,
        verificationToken: activeVerificationToken,
        language: selectedLanguage
      });
      const connection = response.data;
      const room = new Room({adaptiveStream: true, dynacast: true});
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        attachRemoteAudioTrack(track);
      });
      room.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        detachRemoteAudioTrack(track);
      });
      room.on(RoomEvent.DataReceived, handleDataReceived as any);
      room.on(RoomEvent.Disconnected, () => {
        setIsRoomLive(false);
        setIsAgentTyping(false);
        clearUiDirective();
        activeAgentStreamsRef.current = 0;
        roomRef.current = null;
        if (endedByUserRef.current || statusRef.current !== 'connected') {
          return;
        }
        if (!hasConversationHappened(messagesRef.current)) {
          return;
        }
        showSuccessScreen();
      });
      registerTextStreamHandler(room);
      registerTranscriptionStreamHandler(room);
      registerTranscriptionHandler(room);

      await room.connect(connection.serverUrl, connection.participantToken);

      const agentStart = startConnectedAgent(connection);

      if (interactionMode === 'voice') {
        await enableMicrophone(room);
      }

      room.remoteParticipants.forEach(participant => {
        participant.trackPublications.forEach(publication => {
          if (publication.track) {
            attachRemoteAudioTrack(publication.track as RemoteTrack);
          }
        });
      });

      try {
        await agentStart;
      } catch (agentError) {
        setSessionInlineError(
          agentError?.message || 'Unable to start the assistant.'
        );
      }

      setIsRoomLive(true);
      setStatus('connected');
    } catch (e) {
      cleanupRoom();
      setIsRoomLive(false);
      setIsAgentTyping(false);
      clearUiDirective();
      activeAgentStreamsRef.current = 0;
      setStatus('welcome');
      setWelcomeInlineError(
        e?.message || 'Unable to start the assistant session.'
      );
    }
  };

  const endSession = () => {
    endedByUserRef.current = true;
    cleanupRoom();
    resetSessionView();
    setPatientPhone(prefilledPhone);
    resetPhoneVerification();
    setIsRoomLive(false);
    setStatus('welcome');
  };

  const toggleMicrophone = async () => {
    const room = roomRef.current;
    if (!room) {
      return;
    }
    if (micEnabled) {
      await room.localParticipant.setMicrophoneEnabled(false);
      setMicEnabled(false);
    } else {
      await enableMicrophone(room);
    }
  };

  const sendChatText = async (messageText: string): Promise<boolean> => {
    const message = messageText.trim();
    const room = roomRef.current;
    if (!message || !room || status !== 'connected' || !isRoomLive) {
      return false;
    }

    setMessageDraft('');
    appendMessage({origin: 'local', message});
    setIsAgentTyping(true);
    setSessionInlineError('');
    clearUiDirective();
    try {
      const sendText = (room.localParticipant as any).sendText;
      if (typeof sendText === 'function') {
        await sendText.call(room.localParticipant, message, {
          topic: CHAT_TOPIC
        });
      } else {
        await room.localParticipant.publishData(
          new TextEncoder().encode(
            JSON.stringify({
              id: makeMessageId('chat'),
              timestamp: Date.now(),
              message
            })
          ),
          {reliable: true, topic: CHAT_TOPIC} as any
        );
      }
      return true;
    } catch (e) {
      setIsAgentTyping(false);
      setSessionInlineError(e?.message || 'Unable to send message.');
      return false;
    }
  };

  const sendChatMessage = async () => {
    await sendChatText(messageDraft);
    focusChatInput();
  };

  const handleUiOptionSelect = async (option: UiOption) => {
    if (answeredUiOptionId || status !== 'connected' || !isRoomLive) {
      return;
    }
    setAnsweredUiOptionId(option.id);
    const sent = await sendChatText(option.send_value);
    if (!sent) {
      setAnsweredUiOptionId('');
    }
    focusChatInput();
  };

  const handleUiDateSelect = async (value: string, directive: UiDirective) => {
    if (answeredUiOptionId) {
      return;
    }
    const date = parseDateInputValue(value);
    if (!date) {
      return;
    }

    if (directive.kind === 'dob_select') {
      const {minDate, maxDate} = getDobBounds(directive.dobConstraints);
      if (date < minDate || date > maxDate) {
        setUiDateError('Select a valid date of birth.');
        return;
      }
      setUiDateError('');
      const sendValue = toDateInputValue(date);
      await handleUiOptionSelect({
        id: sendValue,
        label: formatDateWithOrdinal(date),
        send_value: sendValue
      });
      return;
    }

    if (!isDateSelectable(date, directive.constraints)) {
      setUiDateError('Select an available date.');
      return;
    }
    setUiDateError('');
    const sendValue = formatDateWithOrdinal(date);
    await handleUiOptionSelect({
      id: sendValue,
      label: sendValue,
      send_value: sendValue
    });
  };

  commitDesktopDateRef.current = (value: string) => {
    if (
      !uiDirective ||
      (uiDirective.kind !== 'date_select' && uiDirective.kind !== 'dob_select')
    ) {
      return;
    }
    void handleUiDateSelect(value, uiDirective);
  };

  useEffect(() => {
    // Chrome fires the input event while the month arrows or month menu are
    // used, and the empty birth-date field then becomes that month plus
    // yesterday. The native change event fires only when a day is chosen.
    if (isIosDevice()) {
      return undefined;
    }
    const input = dateInputRef.current;
    if (
      !input ||
      !uiDirective ||
      (uiDirective.kind !== 'date_select' && uiDirective.kind !== 'dob_select')
    ) {
      return undefined;
    }
    const onNativeChange = () => {
      const nextValue = input.value;
      if (!nextValue) {
        return;
      }
      commitDesktopDateRef.current(nextValue);
    };
    input.addEventListener('change', onNativeChange);
    return () => input.removeEventListener('change', onNativeChange);
  }, [uiDirective]);

  function renderPortalHosTitle() {
    return (
      clinicConfig?.clinic_details?.name ||
      clinicConfig?.clinic_group_details?.group_name ||
      'Practice'
    );
  }

  function renderAddressLine1() {
    const clinic = clinicConfig?.clinic_details;
    return HelperUtils.formatFullAddress({
      line1: clinic?.address,
      line2: clinic?.address_line_2 || '',
      city: '',
      state: '',
      country: '',
      zip: ''
    });
  }

  function renderAddressLine2() {
    const clinic = clinicConfig?.clinic_details;
    return HelperUtils.formatFullAddress({
      line1: '',
      line2: '',
      city: clinic?.city || '',
      state: clinic?.state || '',
      country: '',
      zip: (clinic?.zip || '').toString()
    });
  }

  function renderPracticePhones(): ReactNode {
    const clinicDetails = clinicConfig?.clinic_details;
    if (!clinicDetails) {
      return null;
    }

    const formatPhone = (phone: string): string =>
      phone.startsWith('+1') ? phone : `+1${phone}`;

    if (clinicDetails.phone === clinicDetails.billing_phone) {
      return clinicDetails.phone ? (
        <div className="clinic-phone">
          <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
        </div>
      ) : null;
    }

    return (
      <>
        {clinicDetails.phone ? (
          <div className="clinic-phone">
            <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
          </div>
        ) : null}
        {clinicDetails.billing_phone ? (
          <div className="clinic-phone">
            <PhoneFormatter value={formatPhone(clinicDetails.billing_phone)} />
          </div>
        ) : null}
      </>
    );
  }

  const renderFeaturesPanel = (): ReactElement => {
    const features = [
      {
        icon: <CheckCircleOutlineIcon />,
        title: t('VOICE_ASSISTANT_WEB.FEATURE_RECOGNIZES_TITLE'),
        description: t('VOICE_ASSISTANT_WEB.FEATURE_RECOGNIZES_DESC')
      },
      {
        icon: <EventIcon />,
        title: t('VOICE_ASSISTANT_WEB.FEATURE_BOOKS_TITLE'),
        description: t('VOICE_ASSISTANT_WEB.FEATURE_BOOKS_DESC')
      },
      {
        icon: <ChatBubbleOutlineIcon />,
        title: t('VOICE_ASSISTANT_WEB.FEATURE_ANSWERS_TITLE'),
        description: t('VOICE_ASSISTANT_WEB.FEATURE_ANSWERS_DESC')
      },
      {
        icon: <AccessTimeIcon />,
        title: t('VOICE_ASSISTANT_WEB.FEATURE_AVAILABLE_TITLE'),
        description: t('VOICE_ASSISTANT_WEB.FEATURE_AVAILABLE_DESC')
      }
    ];

    return (
      <div className="voice-assistant-features-panel">
        <h2 className="voice-assistant-features-title">
          <Trans
            i18nKey="VOICE_ASSISTANT_WEB.FEATURES_TITLE"
            components={[
              <span className="features-highlight" key="highlight" />
            ]}
          />
        </h2>
        <ul className="voice-assistant-feature-list">
          {features.map(feature => (
            <li key={feature.title} className="voice-assistant-feature-item">
              <div className="voice-assistant-feature-icon">{feature.icon}</div>
              <div className="voice-assistant-feature-copy">
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            </li>
          ))}
        </ul>
        <hr className="voice-assistant-feature-divider" />
        <p className="voice-assistant-feature-footnote">
          {t('VOICE_ASSISTANT_WEB.FEATURE_FOOTNOTE')}
        </p>
      </div>
    );
  };

  const handleWelcomeFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'connecting' || isSendingOtp || isVerifyingOtp) {
      return;
    }
    if (phoneVerificationStep === 'phone') {
      void handleSendOtp(false);
      return;
    }
    if (phoneVerificationStep === 'otp') {
      void handleVerifyOtp();
    }
  };

  const renderWelcomePanel = (): ReactElement => {
    const isPhoneVerified = phoneVerificationStep === 'verified';

    return (
      <div className="voice-assistant-form-panel">
        <p className="voice-assistant-form-note voice-assistant-form-note--welcome">
          {t('VOICE_ASSISTANT_WEB.CONTACT_NOTE', {
            clinicName: renderPortalHosTitle()
          })}
        </p>
        {welcomeInlineError ? (
          <div className="voice-assistant-inline-error" role="alert">
            <ErrorOutlineIcon fontSize="small" />
            <span>{welcomeInlineError}</span>
          </div>
        ) : null}
        <p className="voice-assistant-field-label">
          {t('VOICE_ASSISTANT_WEB.LANGUAGE')}
        </p>
        <FormControl
          className="voice-assistant-language-select"
          variant="outlined"
          fullWidth>
          <Select
            id="voice-assistant-language"
            value={selectedLanguage}
            onChange={event => setSelectedLanguage(String(event.target.value))}
            displayEmpty
            inputProps={{
              'aria-label': t('VOICE_ASSISTANT_WEB.LANGUAGE')
            }}>
            {languageOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <p className="voice-assistant-field-label">
          {t('VOICE_ASSISTANT_WEB.PATIENT_PHONE')}
          <span className="required-mark"> *</span>
        </p>
        <form
          className="voice-assistant-verification-form"
          noValidate
          onSubmit={handleWelcomeFormSubmit}>
          <div className="voice-assistant-phone-input">
            <PhoneInputField
              value={patientPhone}
              error={Boolean(phoneError)}
              inputProps={{
                name: 'patientPhone',
                id: 'voice-assistant-patient-phone',
                'aria-label': t('VOICE_ASSISTANT_WEB.PATIENT_PHONE'),
                'aria-invalid': Boolean(phoneError),
                'aria-describedby': phoneError
                  ? 'voice-assistant-patient-phone-error'
                  : undefined
              }}
              disableCountryCode={false}
              disableCountryGuess={false}
              country="us"
              onlyCountries={undefined}
              onChange={value => handlePatientPhoneChange(value || '')}
              onEnterKeyPress={event => {
                event.preventDefault();
                if (
                  phoneVerificationStep === 'phone' &&
                  !isSendingOtp &&
                  status !== 'connecting'
                ) {
                  void handleSendOtp(false);
                }
              }}
            />
            {phoneError && (
              <FormHelperText
                id="voice-assistant-patient-phone-error"
                className="voice-assistant-phone-error"
                error>
                {phoneError}
              </FormHelperText>
            )}
          </div>

          {!isPhoneVerified && phoneVerificationStep === 'phone' && (
            <Button
              type="submit"
              color="primary"
              variant="contained"
              className="voice-assistant-start-button voice-assistant-start-button--full"
              disabled={isSendingOtp || status === 'connecting'}>
              {isSendingOtp ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                t('VOICE_ASSISTANT_WEB.SEND_VERIFICATION_CODE')
              )}
            </Button>
          )}

          {!isPhoneVerified && phoneVerificationStep === 'otp' && (
            <div className="voice-assistant-otp-section">
              <div className="voice-assistant-inline-success" role="status">
                <CheckCircleOutlineIcon fontSize="small" />
                <span>
                  {verificationSuccessMessage ||
                    t('VOICE_ASSISTANT_WEB.VERIFICATION_CODE_SENT')}
                </span>
              </div>
              <p className="voice-assistant-field-label voice-assistant-field-label--otp">
                {t('VOICE_ASSISTANT_WEB.VERIFICATION_CODE')}
                <span className="required-mark"> *</span>
              </p>
              <TextField
                id="voice-assistant-otp"
                className="voice-assistant-otp-field"
                fullWidth
                variant="outlined"
                placeholder={t(
                  'VOICE_ASSISTANT_WEB.VERIFICATION_CODE_PLACEHOLDER'
                )}
                value={otp}
                error={Boolean(otpError)}
                helperText={otpError}
                onChange={event => {
                  setOtp(event.target.value.replace(/\D/g, '').slice(0, 6));
                  if (otpError) {
                    setOtpError('');
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 6,
                  'aria-label': t('VOICE_ASSISTANT_WEB.VERIFICATION_CODE')
                }}
              />
              <div className="voice-assistant-otp-resend-row">
                {resendCooldownRemaining > 0 ? (
                  <span className="voice-assistant-resend-countdown">
                    {t('VOICE_ASSISTANT_WEB.RESEND_CODE_AVAILABLE_IN', {
                      seconds: resendCooldownRemaining
                    })}
                  </span>
                ) : (
                  <button
                    type="button"
                    className="voice-assistant-resend-link"
                    disabled={isSendingOtp || status === 'connecting'}
                    onClick={() => handleSendOtp(true)}>
                    {isSendingOtp
                      ? t('VOICE_ASSISTANT_WEB.RESENDING_VERIFICATION_CODE')
                      : t('VOICE_ASSISTANT_WEB.RESEND_VERIFICATION_CODE')}
                  </button>
                )}
              </div>
              <div className="voice-assistant-otp-actions voice-assistant-otp-actions--verify">
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  className="voice-assistant-start-button voice-assistant-start-button--full"
                  disabled={isVerifyingOtp || status === 'connecting'}>
                  {isVerifyingOtp ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : (
                    t('VOICE_ASSISTANT_WEB.VERIFY_CODE')
                  )}
                </Button>
              </div>
            </div>
          )}
        </form>

        {isPhoneVerified && clinicConfig?.voice_button_enabled !== false && (
          <>
            <div
              className="voice-assistant-inline-success voice-assistant-inline-success--verified"
              role="status">
              <CheckCircleOutlineIcon fontSize="small" />
              <span>
                {verificationSuccessMessage ||
                  t('VOICE_ASSISTANT_WEB.PHONE_VERIFIED')}
              </span>
            </div>

            <div className="voice-assistant-start-actions">
              <Button
                color="primary"
                variant="contained"
                className="voice-assistant-start-button"
                startIcon={<ChatIcon />}
                disabled={status === 'connecting'}
                onClick={() => startSession('chat')}>
                {t('VOICE_ASSISTANT_WEB.START_CHAT')}
              </Button>
              <Button
                color="primary"
                variant="outlined"
                className="voice-assistant-start-button voice-assistant-start-button--voice"
                startIcon={<PhoneIcon />}
                disabled={status === 'connecting'}
                onClick={() => startSession('voice')}>
                {t('VOICE_ASSISTANT_WEB.START_VOICE')}
              </Button>
            </div>
          </>
        )}

        {status === 'connecting' && (
          <div className="voice-assistant-connecting">
            <CircularProgress size={20} />
            <span>{t('VOICE_ASSISTANT_WEB.CONNECTING')}</span>
          </div>
        )}

        <div className="voice-assistant-trust-badges">
          <span className="voice-assistant-trust-badge">
            <LockOutlinedIcon />
            {t('VOICE_ASSISTANT_WEB.HIPAA_SECURE')}
          </span>
          <span className="voice-assistant-trust-badge">
            <AccessTimeIcon />
            {t('VOICE_ASSISTANT_WEB.REPLIES_IN_SECONDS')}
          </span>
        </div>
      </div>
    );
  };

  const renderSuccessPanel = (): ReactElement => (
    <div className="voice-assistant-success-card">
      <div className="voice-assistant-success-icon" aria-hidden="true">
        <CheckCircleOutlineIcon />
      </div>
      <Typography
        variant="h4"
        component="h2"
        className="voice-assistant-success-title">
        {t('VOICE_ASSISTANT_WEB.SUCCESS_GENERIC_TITLE')}
      </Typography>
      <div className="voice-assistant-success-summary">
        <Typography
          variant="caption"
          component="p"
          className="voice-assistant-success-summary-label">
          {t('VOICE_ASSISTANT_WEB.SUCCESS_SUMMARY_LABEL')}
        </Typography>
        <Typography variant="body1" component="p">
          {t('VOICE_ASSISTANT_WEB.SUCCESS_SUMMARY_GENERIC')}
        </Typography>
      </div>
      <Button
        color="primary"
        variant="contained"
        className="voice-assistant-success-done-button"
        onClick={endSession}>
        {t('VOICE_ASSISTANT_WEB.DONE_BUTTON')}
      </Button>
    </div>
  );

  const uiOptionsDisabled =
    Boolean(answeredUiOptionId) || status !== 'connected' || !isRoomLive;
  const isMobileChatLayout = activeMode === 'chat' && isMobileViewport;
  const isChatComposerActive = isMobileChatLayout && isMobileKeyboardOpen;
  // iPhone opens zoomed out, so the logo, footer, and rounded card read as a
  // border. Use the same full-screen frame as the keyboard from the start.
  const isIosChatFullScreen =
    isSessionPanelOpen && isMobileChatLayout && isIosDevice();

  const renderDateUiOptions = (): ReactElement | null => {
    if (
      !uiDirective ||
      activeMode !== 'chat' ||
      (uiDirective.kind !== 'date_select' && uiDirective.kind !== 'dob_select')
    ) {
      return null;
    }

    const bounds =
      uiDirective.kind === 'dob_select'
        ? getDobBounds(uiDirective.dobConstraints)
        : getDateBounds(uiDirective.constraints);
    const dateChips =
      uiDirective.kind === 'date_select' ? uiDirective.options : [];
    return (
      <div
        className={
          'voice-assistant-message voice-assistant-message--remote ' +
          'voice-assistant-message--ui-options voice-assistant-ui-options--date ' +
          'voice-assistant-ui-card' +
          (dateChips.length > 0
            ? ' voice-assistant-ui-options--date-with-chips'
            : '')
        }>
        {shouldShowUiPrompt(uiDirective) ? (
          <Typography variant="body2" className="voice-assistant-ui-prompt">
            {uiDirective.prompt}
          </Typography>
        ) : (
          <Typography variant="body2" className="voice-assistant-ui-prompt">
            {uiDirective.kind === 'dob_select'
              ? 'Date of birth'
              : 'Select date'}
          </Typography>
        )}
        <div className="voice-assistant-ui-date-row">
          <div
            className={
              'voice-assistant-ui-date-field-wrap' +
              (uiDateValue ? '' : ' voice-assistant-ui-date-field-wrap--empty')
            }>
            {!uiDateValue ? (
              <span
                className="voice-assistant-ui-date-placeholder"
                aria-hidden="true">
                MM/DD/YYYY
              </span>
            ) : null}
            <TextField
              key={uiDirective.directive_id}
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              inputRef={dateInputRef}
              {...(isIosDevice() ? {value: uiDateValue} : {defaultValue: ''})}
              className="voice-assistant-ui-date-field"
              disabled={uiOptionsDisabled}
              error={Boolean(uiDateError)}
              helperText={uiDateError}
              onClick={event => {
                if (uiOptionsDisabled) {
                  return;
                }
                const input = event.currentTarget.querySelector('input');
                if (input instanceof HTMLInputElement) {
                  openNativeDatePicker(input);
                }
              }}
              onFocus={() => {
                dateFieldFocusedAtRef.current = Date.now();
                dateFieldTouchedRef.current = false;
              }}
              onChange={
                isIosDevice()
                  ? event => {
                      const nextValue = event.target.value;
                      const isOpeningStamp =
                        Date.now() - dateFieldFocusedAtRef.current < 500;
                      const parsed = parseDateInputValue(nextValue);
                      const isDob = uiDirective.kind === 'dob_select';
                      let isDisallowedDob = false;
                      if (isDob) {
                        const {minDate, maxDate} = getDobBounds(
                          uiDirective.dobConstraints
                        );
                        isDisallowedDob =
                          !parsed || parsed < minDate || parsed > maxDate;
                      }
                      // iOS writes today into an empty date input as the picker opens.
                      if (isOpeningStamp || isDisallowedDob) {
                        event.target.value = uiDateValue;
                        return;
                      }
                      setUiDateValue(nextValue);
                      dateFieldTouchedRef.current = true;
                      if (uiDateError) {
                        setUiDateError('');
                      }
                    }
                  : undefined
              }
              onBlur={
                isIosDevice()
                  ? event => {
                      const ignoredOpen =
                        Date.now() - dateFieldFocusedAtRef.current < 500 ||
                        !dateFieldTouchedRef.current;
                      if (ignoredOpen) {
                        event.target.value = uiDateValue;
                        return;
                      }
                      void handleUiDateSelect(event.target.value, uiDirective);
                    }
                  : undefined
              }
              inputProps={{
                min: toDateInputValue(bounds.minDate),
                max: toDateInputValue(bounds.maxDate),
                placeholder: 'MM/DD/YYYY',
                'aria-label':
                  uiDirective.kind === 'dob_select' ? 'Date of birth' : 'Date'
              }}
            />
          </div>
          {dateChips.length > 0
            ? renderOptionButtons(
                dateChips,
                'voice-assistant-quick-reply-button'
              )
            : null}
        </div>
      </div>
    );
  };

  const renderOptionButtons = (
    options: UiOption[],
    buttonClassName: string
  ): ReactElement => (
    <div
      className={
        buttonClassName === 'voice-assistant-quick-reply-button'
          ? 'voice-assistant-quick-replies-list'
          : 'voice-assistant-ui-option-list'
      }>
      {options.map(option => (
        <Button
          key={option.id}
          variant={answeredUiOptionId === option.id ? 'contained' : 'outlined'}
          color="primary"
          className={buttonClassName}
          disabled={uiOptionsDisabled}
          onMouseDown={event => event.preventDefault()}
          onClick={() => handleUiOptionSelect(option)}>
          {option.label}
        </Button>
      ))}
    </div>
  );

  const toggleChoice = (id: string) => {
    if (!uiDirective || uiOptionsDisabled) {
      return;
    }
    setPickedChoiceIds(current => {
      if (uiDirective.multiple) {
        return current.includes(id)
          ? current.filter(item => item !== id)
          : [...current, id];
      }
      return current[0] === id ? [] : [id];
    });
  };

  const submitChoice = () => {
    if (!uiDirective || uiOptionsDisabled) {
      return;
    }
    const chosen = uiDirective.options.filter(option =>
      pickedChoiceIds.includes(option.id)
    );
    const extra = otherChoiceOn ? otherChoiceText.trim() : '';
    if (!chosen.length && !extra) {
      return;
    }
    const parts = chosen.map(option => option.send_value);
    if (extra) {
      parts.push(extra);
    }
    const text = parts.join(', ');
    void handleUiOptionSelect({id: 'submit', label: text, send_value: text});
  };

  const renderDeferredChoice = (className: string): ReactElement => {
    const directive = uiDirective as UiDirective;
    const extra = otherChoiceOn ? otherChoiceText.trim() : '';
    const canSend = pickedChoiceIds.length > 0 || extra.length > 0;
    return (
      <div className={className} role="group" aria-label="Quick replies">
        {shouldShowUiPrompt(directive) ? (
          <Typography variant="body2" className="voice-assistant-ui-prompt">
            {directive.prompt}
          </Typography>
        ) : null}
        <div className="voice-assistant-quick-replies-list">
          {directive.options.map(option => (
            <Button
              key={option.id}
              variant={
                pickedChoiceIds.includes(option.id) ? 'contained' : 'outlined'
              }
              color="primary"
              className="voice-assistant-quick-reply-button"
              disabled={uiOptionsDisabled}
              onMouseDown={event => event.preventDefault()}
              onClick={() => toggleChoice(option.id)}>
              {option.label}
            </Button>
          ))}
          {directive.other ? (
            <Button
              variant={otherChoiceOn ? 'contained' : 'outlined'}
              color="primary"
              className="voice-assistant-quick-reply-button"
              disabled={uiOptionsDisabled}
              onMouseDown={event => event.preventDefault()}
              onClick={() => setOtherChoiceOn(current => !current)}>
              {directive.other_label || 'Other'}
            </Button>
          ) : null}
        </div>
        {directive.other && otherChoiceOn ? (
          <TextField
            className="voice-assistant-choice-other"
            value={otherChoiceText}
            disabled={uiOptionsDisabled}
            variant="outlined"
            size="small"
            fullWidth
            inputProps={{'aria-label': directive.other_label || 'Other'}}
            onChange={event => setOtherChoiceText(event.target.value)}
          />
        ) : null}
        <Button
          variant="contained"
          color="primary"
          className="voice-assistant-choice-continue"
          disabled={uiOptionsDisabled || !canSend}
          onClick={submitChoice}>
          {directive.submit_label || 'Continue'}
        </Button>
      </div>
    );
  };

  const renderChoiceOptions = (className: string): ReactElement | null => {
    if (!uiDirective || !isQuickReplyDirective(uiDirective)) {
      return null;
    }

    if (choiceWaitsForSubmit(uiDirective)) {
      return renderDeferredChoice(className);
    }

    return (
      <div className={className} role="group" aria-label="Quick replies">
        {shouldShowUiPrompt(uiDirective) ? (
          <Typography variant="body2" className="voice-assistant-ui-prompt">
            {uiDirective.prompt}
          </Typography>
        ) : null}
        {renderOptionButtons(
          uiDirective.options,
          'voice-assistant-quick-reply-button'
        )}
      </div>
    );
  };

  const renderThreadUiOptions = (): ReactElement | null => {
    if (!uiDirective || activeMode !== 'chat') {
      return null;
    }

    // Phones keep choices above the composer. Tablets and desktops show them
    // under the question in the transcript.
    if (isQuickReplyDirective(uiDirective)) {
      if (isPhoneViewport) {
        return null;
      }
      return renderChoiceOptions('voice-assistant-choice-options');
    }

    if (
      uiDirective.kind === 'date_select' ||
      uiDirective.kind === 'dob_select'
    ) {
      return renderDateUiOptions();
    }

    if (!isThreadUiDirective(uiDirective)) {
      return null;
    }

    return (
      <div
        className={
          `voice-assistant-message voice-assistant-message--remote ` +
          `voice-assistant-message--ui-options voice-assistant-ui-card ` +
          `voice-assistant-ui-options--${uiDirective.kind}`
        }>
        {shouldShowUiPrompt(uiDirective) && (
          <Typography variant="body2" className="voice-assistant-ui-prompt">
            {uiDirective.prompt}
          </Typography>
        )}
        {renderOptionButtons(
          uiDirective.options,
          'voice-assistant-ui-option-button'
        )}
      </div>
    );
  };

  const renderQuickReplies = (): ReactElement | null => {
    if (
      !uiDirective ||
      activeMode !== 'chat' ||
      !isQuickReplyDirective(uiDirective) ||
      !isPhoneViewport
    ) {
      return null;
    }

    return renderChoiceOptions('voice-assistant-quick-replies');
  };

  const renderSessionPanel = (): ReactElement => {
    const sessionCardClassName = [
      'voice-assistant-session-card',
      isMobileChatLayout ? 'voice-assistant-session-card--chat' : '',
      isChatComposerActive ? 'voice-assistant-session-card--condensed' : ''
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={sessionCardClassName}>
        <div className="voice-assistant-session-header">
          {isChatComposerActive ? (
            <>
              <div className="voice-assistant-session-header-condensed">
                <span
                  className="voice-assistant-status-dot"
                  aria-hidden="true"
                />
                <span className="voice-assistant-session-header-condensed-text">
                  <span className="voice-assistant-session-header-brand">
                    ReachMyDr
                  </span>
                  <span aria-hidden="true"> · </span>
                  <span>Connected</span>
                </span>
              </div>
              {status === 'connected' && (
                <button
                  type="button"
                  className="voice-assistant-session-end-text"
                  onClick={endSession}>
                  End
                </button>
              )}
            </>
          ) : (
            <>
              <div>
                <Typography variant="h5">
                  {activeMode === 'voice' ? 'Voice call' : 'Chat session'}
                </Typography>
                <div className="voice-assistant-session-status">
                  <span
                    className="voice-assistant-status-dot"
                    aria-hidden="true"
                  />
                  <Typography
                    variant="body2"
                    component="span"
                    className="voice-assistant-session-status-label">
                    Connected
                  </Typography>
                </div>
              </div>
              <div className="voice-assistant-session-actions">
                {activeMode === 'voice' &&
                  status === 'connected' &&
                  isRoomLive && (
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={micEnabled ? <MicIcon /> : <MicOffIcon />}
                      onClick={toggleMicrophone}>
                      {micEnabled ? 'Mute' : 'Unmute'}
                    </Button>
                  )}
                {status === 'connected' && (
                  <Button
                    variant="contained"
                    color={isMobileChatLayout ? 'primary' : 'secondary'}
                    className={
                      isMobileChatLayout
                        ? 'voice-assistant-session-end-button'
                        : undefined
                    }
                    startIcon={
                      activeMode === 'voice' || !isMobileChatLayout ? (
                        <CallEndIcon />
                      ) : undefined
                    }
                    onClick={endSession}>
                    End
                  </Button>
                )}
              </div>
            </>
          )}
        </div>

        <div
          className="voice-assistant-transcript"
          ref={transcriptContainerRef}>
          {messages.length === 0 && !isAgentTyping && (
            <div className="voice-assistant-empty-transcript">
              <Typography>Waiting for the assistant...</Typography>
            </div>
          )}
          {messages.map((item, index) => (
            <div
              key={item.id}
              className={`voice-assistant-message voice-assistant-message--${item.origin}`}>
              {item.origin === 'remote' ? (
                <div
                  className="voice-assistant-message-markdown"
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(item.message)
                  }}
                />
              ) : (
                <div>{item.message}</div>
              )}
              {!isMobileChatLayout ||
              shouldShowMessageTimestamp(messages, index) ? (
                <span>{formatMessageTime(item.timestamp)}</span>
              ) : null}
            </div>
          ))}
          {renderThreadUiOptions()}
          {activeMode === 'chat' && isAgentTyping && (
            <div
              className="voice-assistant-typing-indicator"
              role="status"
              aria-label="Assistant is typing">
              <span aria-hidden="true" />
              <span aria-hidden="true" />
              <span aria-hidden="true" />
            </div>
          )}
          <div
            className="voice-assistant-transcript-anchor"
            aria-hidden="true"
          />
        </div>

        {activeMode === 'chat' && (
          <>
            {sessionInlineError ? (
              <div
                className="voice-assistant-inline-error voice-assistant-inline-error--session"
                role="alert">
                <ErrorOutlineIcon fontSize="small" />
                <span>{sessionInlineError}</span>
              </div>
            ) : null}
            {renderQuickReplies()}
            <div className="voice-assistant-composer">
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Type a message"
                value={messageDraft}
                disabled={status !== 'connected'}
                inputRef={chatInputRef}
                onFocus={() => {
                  window.requestAnimationFrame(scrollTranscriptToBottom);
                }}
                onChange={event => setMessageDraft(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendChatMessage();
                  }
                }}
              />
              <Button
                color="primary"
                variant="contained"
                className={
                  isMobileChatLayout
                    ? 'voice-assistant-composer-send'
                    : undefined
                }
                disabled={
                  status !== 'connected' || !isRoomLive || !messageDraft.trim()
                }
                onClick={sendChatMessage}>
                <SendIcon />
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-centering voice-assistant-loading">
        <Loader size={56} color="primary" />
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="flex-centering voice-assistant-loading">
        <section className="voice-assistant-error-panel">
          <Typography variant="h5">
            This assistant could not be opened
          </Typography>
          <Typography>{pageError}</Typography>
        </section>
      </div>
    );
  }

  const pageClassName = [
    'voice-assistant-page',
    isSessionPanelOpen ? 'voice-assistant-page--session' : '',
    isMobileChatLayout ? 'voice-assistant-page--chat' : '',
    isIosChatFullScreen ? 'voice-assistant-page--ios-fill' : '',
    isChatComposerActive ? 'voice-assistant-page--composer-active' : '',
    isViewportZoomed ? 'voice-assistant-page--zoomed' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <PublicPageLayout
      className={pageClassName}
      testId="pcl-voice-assistantPage"
      practiceBadgeLabel={renderPortalHosTitle()}
      showHero={!isSessionPanelOpen}
      heroBadge={
        <>
          <FlashOnIcon fontSize="small" />
          <span>{t('VOICE_ASSISTANT_WEB.AI_BADGE')}</span>
        </>
      }
      title={
        <Trans
          i18nKey="VOICE_ASSISTANT_WEB.HERO_TITLE"
          components={[<span className="hero-highlight" key="highlight" />]}
        />
      }
      subtitle={t('VOICE_ASSISTANT_WEB.HERO_SUBTITLE')}
      contentLayout={isSessionPanelOpen ? 'session' : 'card'}
      practiceName={renderPortalHosTitle()}
      practiceSubtitle={t('VOICE_ASSISTANT_WEB.CLINIC_TAGLINE')}
      addressLine1={renderAddressLine1()}
      addressLine2={renderAddressLine2()}
      practicePhones={renderPracticePhones()}
      showPracticeInfo={!isSessionPanelOpen}
      afterContent={
        <div ref={audioContainerRef} className="voice-assistant-audio" />
      }>
      {isSessionPanelOpen ? (
        <div className="main-panel voice-assistant-main-panel--session">
          <div className="panel login-panel voice-assistant-session-panel">
            {status === 'completed'
              ? renderSuccessPanel()
              : renderSessionPanel()}
          </div>
        </div>
      ) : (
        <>
          {renderFeaturesPanel()}
          {renderWelcomePanel()}
        </>
      )}
    </PublicPageLayout>
  );
};

export default VoiceAssistant;
